import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddEmailPopup from "./Emails/AddEmailsPopup";
import EmailsRightDrawer from "./Emails/EmailsRightDrawer";
import EmailsBottomDrawer from "./Emails/EmailsBottomDrawer";
import EmailsDetail from "./Emails/EmailsDetail";
import EmailEmptyPage from "./Emails/EmailsEmptyPage";

import { emailsAPI } from "api/Interactions/EmailsAPI";
import { Email } from "api/Interactions/InteractionsInterfaces";

const Emails: React.FC = () => {
  const [emailsData, setEmailsData] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(true);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  useEffect(() => {
    emailsAPI.getAll()
      .then(setEmailsData)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeId) {
      setSelectedEmail(null);
      return;
    }

    emailsAPI.getById(activeId)
      .then(setSelectedEmail)
      .catch(() => setSelectedEmail(null));
  }, [activeId]);

  const handleSelectEmail = (id: number) => {
    navigate(`/interactions/emails/${id}`);
  };

  if (loading)
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}><CircularProgress /></Box>;

  return (
    <GeneralLayout
      useLeftDrawer
      useRightDrawer
      useBottomDrawer
      defaultOpenLeft={true}
      defaultOpenRight={false}
      defaultOpenBottom={false}
      leftDrawerContent={
        <GenericRecordSelection<Email>
          items={emailsData}
          selectedId={activeId ?? undefined}
          onSelect={(email) => handleSelectEmail(email.id)}
          AddPopupComponent={AddEmailPopup}
          onCreated={(newEmail) => {
            setEmailsData((prev) => [...prev, newEmail]);
            navigate(`/interactions/emails/${newEmail.id}`);
          }}
          idKey="id"
          nameKey={(m) => m.name || `Email ${m.id}`}
          colorKey="interactionsPrimary"
          searchPlaceholder="Search emails..."
        />
      }
      rightDrawerContent={activeId ? <EmailsRightDrawer emailId={activeId} /> : null}
      bottomDrawerContent={activeId ? <EmailsBottomDrawer emailId={activeId} /> : null}
    >
      {selectedEmail ? (
        <EmailsDetail
          emailId={activeId!}
          onClose={() => navigate("/interactions/emails/")}
        />
      ) : (
        <EmailEmptyPage
          onEmailsCreated={(newEmail) => navigate(`/interactions/emails/${newEmail.id}`)}
        />
      )}
    </GeneralLayout>
  );
};

export default Emails;
